import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import TitleBanner from './TitleBanner';

const ContactUs = () => {
  const recaptchaRef = React.createRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ type: '', message: '' });

  const handleRecaptchaChange = (token) => {
    setFormData(prev => ({ ...prev, recaptchaToken: token }));
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    typeOfEnquiry: '',
    state: '',
    message: '',
    recaptchaToken: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.recaptchaToken) {
      setFeedback({
        type: 'error',
        message: 'Please complete the reCAPTCHA verification'
      });
      setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 5000);
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch('/submit/contact-us', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setFeedback({
          type: 'success',
          message: 'Thank you for your message. We will get back to you soon!'
        });
        // Reset form after successful submission
        setFormData({
          name: '',
          email: '',
          phone: '',
          typeOfEnquiry: '',
          state: '',
          message: '',
          recaptchaToken: ''
        });
      } else {
        throw new Error('Failed to submit');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFeedback({
        type: 'error',
        message: 'Sorry, something went wrong. Please try again later.'
      });
    } finally {
      recaptchaRef.current.reset();
      setFormData(prev => ({ ...prev, recaptchaToken: '' }));
      setIsSubmitting(false);
      setTimeout(() => {
        setFeedback({ type: '', message: '' });
      }, 5000);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  return (
    <>
    <TitleBanner image="/Website_Title_Images/Contact_Us.svg" title={"Contact Us"} subheading={"We're here for you. Get in touch with our expert team."} />
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-8">
        <h2 className="text-xl font-bold mb-4">Service enquiry:</h2>
        <p className="mb-4">If you are interested in learning more about our services, please fill out the form below and a member from our friendly team will get in touch with you.</p>
        
        <h2 className="text-xl font-bold mb-4">Feedback:</h2>
        <p className="mb-4">If you would like to provide feedback/make a complaint, please email{' '}
          <a href="mailto:info@livabilitycare.com.au" className="text-primary underline">
            info@livabilitycare.com.au
          </a>
          . We have a very clear complaints and feedback policy and take all feedback seriously.
        </p>
        
        <h2 className="text-xl font-bold mb-4">Employment:</h2>
        <p className="mb-4">If you are applying for a job or placement at LCA, please apply via the Careers page.</p>
      </div>

      <div className="bg-white rounded-lg shadow-lg border-primary border-2 p-6 mb-8">
        <h2 className="text-2xl font-bold text-primary mb-6">Lets Connect!</h2>
        
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full p-2 border border-primary rounded"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-2 border border-primary rounded"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-2 border border-primary rounded"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Type of Enquiry</label>
              <select
                name="typeOfEnquiry"
                value={formData.typeOfEnquiry}
                onChange={handleChange}
                className="w-full p-2 border border-primary rounded"
                required
              >
                <option value="">Select an option</option>
                <option value="general">General Enquiry</option>
                <option value="service">Service Enquiry</option>
                <option value="feedback">Feedback</option>
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">State</label>
            <div className="space-y-2">
              {['South Australia', 'Queensland', 'Victoria'].map((state) => (
                <label key={state} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="state"
                    value={state}
                    checked={formData.state === state}
                    onChange={handleChange}
                    className="text-primary"
                    required
                  />
                  <span>{state}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium mb-1">Message</label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              className="w-full p-2 border border-primary rounded h-32"
              required
            />
          </div>

          <div className="mt-4">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6Lcu-tMqAAAAAGwHN42PvnenVU6lYdAh_ikQfuV7"
              onChange={handleRecaptchaChange}
            />
          </div>

          {feedback.message && (
            <div className={`mt-4 p-3 rounded ${
              feedback.type === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {feedback.message}
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-4 bg-secondary-green text-white px-8 py-2 rounded-full hover:bg-green-600 transition-colors disabled:bg-gray-400"
          >
            {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
          </button>
        </form>
      </div>
    </div>
    </>
  );
};

export default ContactUs;